/* Only the active viewport's video is inserted, including after theme-editor reloads. */
if (!customElements.get('waggl-hero')) {
  class WagglHero extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.media = this.querySelector('[data-wg-hero-media]');
      this.toggle = this.querySelector('[data-wg-video-toggle]');
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = true;
      this.userPaused = false;
      this.userStarted = false;
      this.onViewportChange = () => this.selectVideo();
      this.onPreferenceChange = () => this.updatePlayback();
      this.onToggle = () => {
        if (!this.video) return;
        this.userPaused = !this.video.paused;
        if (!this.userPaused) this.userStarted = true;
        this.updatePlayback();
      };
      this.mobile.addEventListener('change', this.onViewportChange);
      this.reducedMotion.addEventListener('change', this.onPreferenceChange);
      document.addEventListener('visibilitychange', this.onPreferenceChange);
      this.toggle?.addEventListener('click', this.onToggle);
      if ('IntersectionObserver' in window) {
        this.observer = new window.IntersectionObserver(entries => {
          this.visible = entries[0]?.isIntersecting ?? false;
          this.updatePlayback();
        }, {threshold: 0});
        this.observer.observe(this);
      }
      this.selectVideo();
    }

    disconnectedCallback() {
      this.mobile?.removeEventListener('change', this.onViewportChange);
      this.reducedMotion?.removeEventListener('change', this.onPreferenceChange);
      document.removeEventListener('visibilitychange', this.onPreferenceChange);
      this.toggle?.removeEventListener('click', this.onToggle);
      this.observer?.disconnect();
      this.removeVideo();
      this.initialized = false;
    }

    removeVideo() {
      this.video?.pause();
      this.video?.remove();
      this.video = null;
      this.sourceTemplate = null;
      if (this.toggle) this.toggle.hidden = true;
    }

    selectVideo() {
      const desktop = this.querySelector('[data-wg-video-template="desktop"]');
      const mobile = this.querySelector('[data-wg-video-template="mobile"]');
      const template = this.mobile.matches ? (mobile || desktop) : desktop;
      if (this.sourceTemplate === template && this.video) return;
      this.removeVideo();
      if (!template || !this.media || !this.toggle) return;
      const video = template.content.firstElementChild?.cloneNode(true);
      if (!video) return;
      this.video = video;
      this.sourceTemplate = template;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.addEventListener('playing', () => {
        video.classList.add('is-ready');
        this.syncControl();
      });
      video.addEventListener('pause', () => this.syncControl());
      video.addEventListener('error', () => {
        if (this.video === video) this.removeVideo();
      });
      this.media.append(video);
      this.toggle.hidden = false;
      this.updatePlayback();
    }

    syncControl() {
      if (!this.toggle || !this.video) return;
      const paused = this.video.paused;
      this.toggle.dataset.paused = String(paused);
      this.toggle.setAttribute('aria-label', paused ? 'Play background video' : 'Pause background video');
    }

    updatePlayback() {
      if (!this.video) return;
      const automaticMotion = !this.reducedMotion.matches && !window.navigator?.connection?.saveData;
      const shouldPlay = (this.userStarted || automaticMotion) && !this.userPaused && this.visible && !document.hidden;
      if (shouldPlay) {
        this.video.preload = 'metadata';
        this.video.play()?.catch(() => this.syncControl());
      } else {
        this.video.pause();
      }
      this.syncControl();
    }
  }
  customElements.define('waggl-hero', WagglHero);
}